import { Skeleton } from './Skeleton';
import { cx } from '../internal/cx';

/**
 * Composições de Skeleton — §17.
 *
 * **O ponto inteiro é preservar a ESTRUTURA do que está carregando.** Um retângulo cinza
 * genérico no lugar de uma tabela não é loading state: é um buraco. Ele não diz quanta
 * coisa vem, nem em que forma, e quando o conteúdo real chega o layout salta. Cada
 * composição aqui desenha o esqueleto do conteúdo que vai substituir, de modo que a troca
 * seja quase imperceptível.
 *
 * Regra do mandato que estas peças existem para viabilizar: **loading é localizado**. O
 * Dashboard inteiro não pode ficar bloqueado porque um gráfico está carregando — cada
 * bloco chega com o próprio esqueleto.
 *
 * O anúncio a leitor de tela vive no `Skeleton` de baixo (`role="status"`), então estas
 * composições não repetem "carregando" N vezes: passam `label` só onde o bloco é a unidade
 * anunciável, e silenciam o resto.
 */

export interface SkeletonPatternProps {
  className?: string;
  /** Anúncio para leitor de tela. Uma vez por bloco, não por linha. */
  label?: string;
}

/** Parágrafo: a última linha é mais curta, como texto real. */
export function SkeletonText({
  lines = 3,
  className,
  label = 'Carregando conteúdo',
}: SkeletonPatternProps & { lines?: number }) {
  return (
    <div className={cx('argink-skeleton-text', className)}>
      <Skeleton lines={Math.max(1, lines - 1)} variant="text" label={label} />
      <Skeleton lines={1} variant="text" width="62%" label="" />
    </div>
  );
}

/** Card: faixa de título, corpo e uma linha de meta no rodapé. */
export function SkeletonCard({ className, label = 'Carregando cartão' }: SkeletonPatternProps) {
  return (
    <div className={cx('argink-skeleton-card', className)}>
      <Skeleton lines={1} variant="text" width="45%" height="1.125rem" label={label} />
      <Skeleton lines={2} variant="text" label="" />
      <Skeleton lines={1} variant="text" width="30%" label="" />
    </div>
  );
}

/** Formulário: pares label+campo, com a altura real de um controle. */
export function SkeletonForm({
  fields = 3,
  className,
  label = 'Carregando formulário',
}: SkeletonPatternProps & { fields?: number }) {
  return (
    <div className={cx('argink-skeleton-form', className)}>
      {Array.from({ length: Math.max(1, fields) }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className="argink-skeleton-form__field">
          <Skeleton
            lines={1}
            variant="text"
            width="34%"
            height="0.8125rem"
            label={index === 0 ? label : ''}
          />
          {/* 2.5rem é a altura real de um Input do package — sem isso o formulário salta
           * quando os campos de verdade chegam. */}
          <Skeleton lines={1} variant="block" height="2.5rem" label="" />
        </div>
      ))}
    </div>
  );
}

/** Tabela: cabeçalho + N linhas com colunas de larguras diferentes, como dados reais. */
export function SkeletonTable({
  rows = 5,
  columns = 4,
  className,
  label = 'Carregando tabela',
}: SkeletonPatternProps & { rows?: number; columns?: number }) {
  const widths = ['32%', '22%', '18%', '14%', '10%'];
  const cols = Math.max(1, columns);

  return (
    <div className={cx('argink-skeleton-table', className)}>
      <div className="argink-skeleton-table__row argink-skeleton-table__row--head">
        {Array.from({ length: cols }).map((_, c) => (
          // eslint-disable-next-line react/no-array-index-key
          <Skeleton
            key={c}
            lines={1}
            variant="text"
            width={widths[c % widths.length]}
            height="0.8125rem"
            label={c === 0 ? label : ''}
          />
        ))}
      </div>
      {Array.from({ length: Math.max(1, rows) }).map((_, r) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={r} className="argink-skeleton-table__row">
          {Array.from({ length: cols }).map((_, c) => (
            // eslint-disable-next-line react/no-array-index-key
            <Skeleton key={c} lines={1} variant="text" width={widths[c % widths.length]} label="" />
          ))}
        </div>
      ))}
    </div>
  );
}
